import api from 'api';
import config from 'config';

const getAll = () => {
  return api.spots
    .getAll()
    .then(r => r.data)
    .then(spots => {
      return spots.map(spot => {
        if (!spot.userPhotos) {
          spot.userPhotos = [];
        }

        if (!spot.userPhotosHydrated) {
          spot.userPhotosHydrated = [];
        }

        spot.userPhotosHydrated = spot.userPhotosHydrated.map(x => ({
          ...x,
          url: `${config.firebaseBaseUrl}${x.file}`.replace('.jpg', '.webp'), //?alt=media
        }));

        return spot;
      });
    });
};

const getAllLight = () => {
  return api.spots.getAllLight().then(r => r.data);
};

const getAllSlugsFromSpotsV19 = () => {
  return api.spots.getAllSlugsFromSpotsV19().then(r => {
    const spots = (r.data && r.data.spots) || [];

    return spots
      .map(spot => ({
        id: spot.i || spot.id,
        slug: spot.s || spot.slug,
      }))
      .filter(spot => spot.id && spot.slug);
  });
};

const getAllSlugs = () => {
  return api.spots.getAllSlugs().then(r => r.data);
};

const getDescriptionTodo = () => {
  return api.spots.getDescriptionTodo().then(r => r.data || null);
};

// The spot universe for Auto Mod: id, slug and coordinates for every spot, and
// nothing else, which is exactly what spots-v19.json already publishes to the
// CDN.
//
// The file is rebuilt on a cron, so it is behind by however long ago that ran -
// create a spot, reload the page, and it would be missing. It is not read as an
// approximation for that reason: it stamps the moment it was built, and the
// spots created after that moment are asked for by name. The result is the same
// catalogue get-all-light returned, without reading the spots collection.
const getUniverse = async () => {
  const catalogue = await api.spots.getSpotsV19().then(r => r.data || {});
  const spots = (catalogue.spots || [])
    .map(spot => {
      const coordinates = Array.isArray(spot.c) ? spot.c : [];

      return {
        id: spot.i,
        slug: spot.s || '',
        lat: Number(coordinates[0]),
        lng: Number(coordinates[1]),
      };
    })
    .filter(spot => spot.id && Number.isFinite(spot.lat) && Number.isFinite(spot.lng));

  const builtAt = Number(catalogue.createdAt);

  if (!Number.isFinite(builtAt) || builtAt <= 0) {
    // Without the build stamp there is no way to know what the file is missing,
    // so the delta cannot be asked for and the catalogue is returned as it came.
    return spots;
  }

  const created = await api.spots
    .getCreatedSince(builtAt)
    .then(r => r.data || [])
    .catch(() => []);
  const known = new Set(spots.map(spot => spot.id));

  created.forEach(spot => {
    if (!spot || !spot.id || known.has(spot.id)) {
      return;
    }

    const lat = Number(spot.lat);
    const lng = Number(spot.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }

    known.add(spot.id);
    spots.push({ id: spot.id, slug: spot.slug || '', lat, lng });
  });

  return spots;
};

const cacheGetHydratedById = {};
const pendingCreations = new Map();

const getHydratedById = spotId => {
  if (cacheGetHydratedById[spotId]) {
    return cacheGetHydratedById[spotId];
  }

  cacheGetHydratedById[spotId] = api.spots
    .getHydratedById(spotId)
    .then(r => r.data)
    .then(({ spot }) => {
      if (!spot.userPhotosHydrated) {
        spot.userPhotosHydrated = [];
      }

      spot.userPhotosHydrated.forEach(x => {
        x.url = `${config.firebaseBaseUrl}${x.file}`.replace('.jpg', '.webp'); //?alt=media
      });

      return { spot };
    });

  return cacheGetHydratedById[spotId];
};

const create = (spot, { suggestionId, ownerPoint } = {}) => {
  const request = suggestionId
    ? api.spots.create(spot, { suggestionId, ownerPoint })
    : api.spots.create(spot);

  return request.then(r => r.data);
};

const createOptimistically = spot => {
  const pendingCreationKey = `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const creationPromise = create(spot);

  pendingCreations.set(pendingCreationKey, {
    creationPromise,
    operationTail: creationPromise.then(() => undefined, () => undefined),
  });

  return {
    pendingSpot: {
      ...spot,
      id: pendingCreationKey,
      _pendingCreationKey: pendingCreationKey,
      _isPendingCreation: true,
    },
    creationPromise,
  };
};

const runAfterCreation = (spot, operation, { serialize = false } = {}) => {
  const pendingCreationKey = spot && spot._pendingCreationKey;
  const pendingCreation = pendingCreationKey && pendingCreations.get(pendingCreationKey);

  if (!pendingCreation) {
    return operation(spot);
  }

  if (!serialize) {
    return pendingCreation.creationPromise.then(createdSpot => operation(createdSpot));
  }

  const request = pendingCreation.operationTail.then(() =>
    pendingCreation.creationPromise.then(createdSpot => operation(createdSpot))
  );

  pendingCreation.operationTail = request.then(() => undefined, () => undefined);
  return request;
};

const edit = (spot, newData) => {
  return runAfterCreation(
    spot,
    createdSpot => api.spots.edit(createdSpot, newData).then(r => r.data),
    { serialize: true }
  );
};

const assign = (spot, photo, { suggestionId } = {}) => {
  return runAfterCreation(spot, createdSpot => {
    const request = suggestionId
      ? api.spots.assign(createdSpot, photo, { suggestionId })
      : api.spots.assign(createdSpot, photo);

    return request.then(r => r.data);
  });
};

const translateDescription = spot => {
  return runAfterCreation(
    spot,
    createdSpot => api.spots.translateDescriptionById(createdSpot.id).then(r => r.data),
    { serialize: true }
  );
};

const translateDescriptionById = spotId => {
  return api.spots.translateDescriptionById(spotId).then(r => r.data);
};

const actionsSpots = {
  getAll,
  getAllLight,
  getAllSlugs,
  getAllSlugsFromSpotsV19,
  getDescriptionTodo,
  getUniverse,
  getHydratedById,
  create,
  createOptimistically,
  edit,
  assign,
  translateDescription,
  translateDescriptionById,
};

export default actionsSpots;
